import { Injectable, OnDestroy } from '@angular/core';
import _ from 'lodash';
import axios from 'axios';
import { TextBoxFactory } from '../models/factories/text-box-factory';
import { Page } from '../models/page';
import { TextBox } from '../models/text-box';
import { TextSelection } from '../models/text-selection';
import { RouteService } from './route.service';
import { environment } from '../../environments/environment';
import { ApplicationStateService } from './application-state.service';
import { RestService } from './rest.service';
import { TextSelectionFactory } from '../models/factories/text-selection-factory';
import { CommunicationService } from './communication.service';
import { BookFeatureDataChangedEvent } from '../utils/events/book-feature-data-changed-event';
import { PageService } from './page.service';
import { Subscription } from 'rxjs';
import { OfflineDataService } from './offline-data.service';
import { CrudService } from './crud.service';
import { TechnicalFeature } from '@lemberger/lemberger-am';

@Injectable({
    providedIn: 'root'
})
export class PageTextService implements OnDestroy {

    /*
    This is used to mark/unmark the last TextBox of a Selection
     */
    private static LAST_TEXTBOX_HIGHLIGHTING = 1;
    /**
     * dummyId is used to assign an id for rects which do not have a real id yet.
     * Start with -2 so, functions which return -1 will not be problematic. Value should be drecremented.
     * @type {number}
     */
    private static dummyId = -2;

    private pageTextJsonMap: Map<number, Map<number, TextBox>> = new Map();
    private textSelections: Array<TextSelection>;
    private cancelTokens: Array<any> = [];
    private subscription: Subscription;

    constructor(private routeService: RouteService,
                private restService: RestService,
                private pageService: PageService,
                private applicationStateService: ApplicationStateService,
                private offlineDataService: OfflineDataService,
                private crudService: CrudService,
                private communicationService: CommunicationService) {

        this.subscription = this.communicationService.pageTurnAnnounced$.subscribe(
            () => {
                this.cancelRequests();
            });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Get all text boxes of a specific page.
     * Fetch if not cached.
     *
     * @param {string} url
     * @param {Page} page
     *
     * @returns {Promise<TextBox[]>}
     */
    getPageTextsOfPage(url: string, page: Page): Promise<Array<TextBox>> {

        if (this.applicationStateService.isOfflineModeEnabled) {

            return new Promise(resolve => {

                const resultAsJSON = this.offlineDataService.getOfflineData().texts;
                const textBoxFactory = new TextBoxFactory();
                const textBoxMap = new Map();
                const textBoxes = [];
                let assignedId = 0;

                if (resultAsJSON[page.index]) {
                    resultAsJSON[page.index].text.forEach(ele => {

                        const obj = textBoxFactory.buildFromObject(ele);
                        if (this.isTextboxBigEnough(obj)) {
                            obj.pageId = page.id;
                            obj.index = assignedId++;
                            textBoxMap.set(obj.index, obj);
                            textBoxes.push(obj);
                        }
                    });
                }

                this.pageTextJsonMap.set(page.id, textBoxMap);
                resolve(textBoxes);
            });
        }

        if (!this.applicationStateService.bookConfig.bookPermissions.pageTexts.view) {
            return new Promise(resolve => {
                resolve([]);
            });
        }

        if (this.pageTextJsonMap && this.pageTextJsonMap.has(page.id)) {
            return new Promise(resolve => {
                resolve(Array.from(this.pageTextJsonMap.get(page.id)
                    .values()));
            });
        }

        const source = axios.CancelToken.source();
        this.cancelTokens.push(source);

        return axios.get(url, {
            withCredentials: true,
            cancelToken: source.token
        })
            .then(result => {

                const resultAsJSON = result.data.text;
                const textBoxFactory = new TextBoxFactory();
                const textBoxMap = new Map();
                const textBoxes = [];
                let assignedId = 0;

                resultAsJSON.forEach(ele => {
                    const obj = textBoxFactory.buildFromObject(ele);

                    if (this.isTextboxBigEnough(obj)) {
                        obj.pageId = page.id;
                        obj.index = assignedId++;
                        textBoxMap.set(obj.index, obj);
                        textBoxes.push(obj);
                    }
                });

                this.pageTextJsonMap.set(page.id, textBoxMap);

                return new Promise(resolve => resolve(textBoxes));
            })
            .catch(err => {
                return new Promise(resolve => resolve([]));
            }) as any;
    }

    /**
     *
     * @param selections
     */
    getTextFromPageSelection(selections: Array<TextBox>): string {
        let rawText = '';
        selections.forEach(textBox => {
            if (textBox.content !== undefined) {
                rawText += textBox.content;
            }
        });

        return rawText;
    }

    /**
     *
     * @param selections
     */
    sortPageSelection(selections: Array<TextBox>): Array<TextBox> {
        return selections.sort((t1, t2) => {
            // Combine the index with page number for the correct position in the selection
            const newIndexOne = parseInt(t1.pageId + '0'.repeat(10 - t1.index.toString().length) + t1.index);
            const newIndexTwo = parseInt(t2.pageId + '0'.repeat(10 - t2.index.toString().length) + t2.index);

            if (newIndexOne > newIndexTwo) {
                return 1;
            }

            if (newIndexOne < newIndexTwo) {
                return -1;
            }

            return 0;
        });
    }

    /**
     * Get a cached text box from a specified page with given index.
     *
     * @param {Page} page
     * @param {number} index
     *
     * @returns TextBox|null
     */
    getTextBoxByPageAndIndex(page: Page, index: number): TextBox | null {
        if (!this.pageTextJsonMap) {
            return null;
        }

        if (!this.pageTextJsonMap.has(page.id)) {
            return null;
        }

        return this.pageTextJsonMap.get(page.id)
            .get(index);
    }

    setTextSelections(textSelections: Array<TextSelection>): void {
        this.textSelections = [];
        textSelections.forEach(element => {
            element.index = this.pageService.getIndexByPageId(element.pageId);
            this.textSelections.push(element);

        });
    }

    /**
     * Similar to setTextSelections(textSelections) but in addition the passed values are transformed in forehand.
     * This method purpose is to do the transformation and the storing in just one for-Loop.
     * @param objects
     */
    setObjectsToTextSelections(objects: Array<any>): void {
        const factory = new TextSelectionFactory();
        this.textSelections = [];
        objects.forEach(element => {
            const entity = factory.buildFromObject(element);
            entity.index = this.pageService.getIndexByPageId(entity.pageId);
            this.textSelections.push(entity);

        });
    }

    getTextSelections(): Promise<Array<TextSelection>> {

        return new Promise(resolve => {
            if (!this.applicationStateService.bookConfig.bookPermissions.pageTexts.view) {

                resolve([]);

                return;
            }

            if (this.textSelections) {
                resolve(this.textSelections);

                return;
            }

            const source = axios.CancelToken.source();
            this.cancelTokens.push(source);
            this.textSelections = [];

            if (this.applicationStateService.isOfflineModeEnabled) {

                const route = this.routeService.getRelativeRoute(environment.endpointUrls.textSelections,
                    {
                        bookId: this.applicationStateService.bookId
                    });

                this.restService.get(route)
                    .then(result => {
                        this.setObjectsToTextSelections(result);
                        resolve(this.textSelections);

                        return;
                    });
            } else {

                const route = this.routeService.getAbsoluteRoute(environment.endpointUrls.textSelections,
                    {
                        bookId: this.applicationStateService.bookId
                    });

                return axios.get(route, { withCredentials: true })
                    .then(result => {
                        this.setObjectsToTextSelections(result.data);
                        resolve(this.textSelections);
                    })
                    .catch(err => {
                        resolve(this.textSelections);
                    }) as any;
            }
        });
    }

    getTextSelectionsOfPage(pageId: number, textBoxes: Array<TextBox>): Promise<Array<TextSelection>> {

        const textSelections = [];

        return new Promise(resolve => {
            this.getTextSelections()
                .then(result => {

                    result.forEach(textSelection => {

                        if (textSelection.pageId === pageId) {
                            const endBox = textBoxes[textSelection.endBox.index];

                            if (endBox) {
                                const textBoxesOfSelection: Array<TextBox> = textBoxes.slice(
                                    textSelection.startBox.index,
                                    textSelection.endBox.index + PageTextService.LAST_TEXTBOX_HIGHLIGHTING
                                );
                                textBoxesOfSelection.forEach(textBoxSelected => {
                                    textBoxSelected.isHighlighted = true;
                                });

                                textSelection.textBoxes = textBoxesOfSelection;
                                textSelection.position = endBox.position;
                                textSelections.push(textSelection);
                            }
                        }
                    });

                    resolve(textSelections);
                });
        });
    }

    getTextCopySelectionsOfPage(pageId: number, textBoxes: Array<TextBox>): Promise<Array<TextSelection>> {

        const textSelections = [];

        return new Promise(resolve => {
            this.getTextSelections()
                .then(result => {

                    result.forEach(textSelection => {

                        if (textSelection.pageId === pageId) {
                            const endBox = textBoxes[textSelection.endBox.index];

                            if (endBox) {
                                const textBoxesOfSelection: Array<TextBox> = textBoxes.slice(
                                    textSelection.startBox.index,
                                    textSelection.endBox.index + PageTextService.LAST_TEXTBOX_HIGHLIGHTING
                                );
                                textBoxesOfSelection.forEach(textBoxSelected => {
                                    textBoxSelected.isSelected = true;
                                });

                                textSelection.textBoxes = textBoxesOfSelection;
                                textSelection.position = endBox.position;
                                textSelections.push(textSelection);
                            }
                        }
                    });

                    resolve(textSelections);
                });
        });
    }

    createTextSelection(textSelection: TextSelection): Promise<TextSelection> {

        return new Promise(resolve => {

            const route = this.routeService.getRelativeRoute(environment.endpointUrls.textSelections,
                {
                    bookId: this.applicationStateService.bookId
                });

            this.assignDummyId(textSelection);
            this.textSelections.push(textSelection);
            const arrayIndex = this.textSelections.length - 1;
            const pageIndex = textSelection.index;
            textSelection.updatedAt = { date: new Date() };

            return this.restService.post(route,
                {
                    startIndex: textSelection.startBox.index,
                    endIndex: textSelection.endBox.index,
                    pageId: textSelection.pageId

                })
                .then(result => {

                    const textSelectionFactory = new TextSelectionFactory();
                    textSelection = textSelectionFactory.buildFromObject(result);
                    textSelection.index = pageIndex;
                    this.textSelections[arrayIndex] = textSelection;
                    this.communicationService.announceBookFeatureDataChanged(BookFeatureDataChangedEvent.createEvent(textSelection));
                    this.crudService.logDirect(TechnicalFeature.TextSelections);
                    resolve(result);

                });
        });
    }

    removeTextSelection(textSelection: TextSelection): Promise<any> {

        return new Promise(resolve => {

            const route = this.routeService.getRelativeRoute(
                environment.endpointUrls.textSelections + environment.crudEndpointsPostfixes.remove,
                {
                    bookId: this.applicationStateService.bookId,
                    id: textSelection.id
                });

            const indexToDelete = this.getIndexOfTextSelection(textSelection);
            this.textSelections.splice(indexToDelete, 1);

            return this.restService.delete(route)
                .then(result => {
                    this.communicationService.announceBookFeatureDataChanged(BookFeatureDataChangedEvent.createEvent(textSelection));
                    this.crudService.logDirect(TechnicalFeature.TextSelections);
                    resolve(result);
                });

        });
    }

    highlightTextBoxesOfSelection(textSelection: TextSelection, textBoxes: Array<TextBox>, pageId: number): void {

        if (!textBoxes) {
            return;
        }

        const textBoxesInSelection = textBoxes.slice(
            textSelection.startBox.index,
            textSelection.endBox.index + PageTextService.LAST_TEXTBOX_HIGHLIGHTING
        );
        textBoxesInSelection.forEach(box => {

            if (textSelection.pageId === pageId) {
                box.pageId = pageId;
                box.isHighlighted = true;
            }
        });

        if (textSelection.pageId === pageId && textBoxesInSelection[textBoxesInSelection.length - 1]) {
            textBoxesInSelection[textBoxesInSelection.length - 1].isEndOfSelection = true;
        }
    }

    selectTextBoxesOfSelection(textSelection: TextSelection, textBoxes: Array<TextBox>, pageId: number): void {

        if (!textBoxes) {
            return;
        }

        const textBoxesInSelection = textBoxes.slice(
            textSelection.startBox.index,
            textSelection.endBox.index + PageTextService.LAST_TEXTBOX_HIGHLIGHTING
        );
        textBoxesInSelection.forEach(box => {

            if (textSelection.pageId === pageId) {
                box.pageId = pageId;
                box.isSelected = true;
            }
        });

        if (textSelection.pageId === pageId && textBoxesInSelection[textBoxesInSelection.length - 1]) {
            textBoxesInSelection[textBoxesInSelection.length - 1].isEndOfSelection = true;
        }
    }

    cancelRequests(): void {

        this.cancelTokens.forEach(ele => {
            if (ele) {
                ele.cancel();
            }
        });

        this.cancelTokens = [];
    }

    private getIndexOfTextSelection(textSelection: TextSelection): number {
        const index = _.findIndex(this.textSelections, { id: textSelection.id });

        return index;
    }

    private assignDummyId(textSelection: TextSelection): void {
        --PageTextService.dummyId;
        textSelection.id = PageTextService.dummyId;
    }

    // This logic is tied to the PageExport logic
    private isTextboxBigEnough(box: TextBox): boolean {
        return box.position.w > 0.3 && box.position.h > 0.99;
    }
}
